import React, { createContext, useContext, useEffect, useState } from 'react';

export type ThemeMode = 'dark' | 'light';
export type ColorAccent = 'cyan' | 'violet' | 'rose' | 'amber' | 'emerald' | 'indigo';

interface ThemeContextValue {
  mode: ThemeMode;
  accent: ColorAccent;
  toggleMode: () => void;
  setMode: (m: ThemeMode) => void;
  setAccent: (a: ColorAccent) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'dark',
  accent: 'cyan',
  toggleMode: () => {},
  setMode: () => {},
  setAccent: () => {},
});

function readStored<T extends string>(key: string, allowed: T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    if (v && allowed.includes(v)) return v;
  } catch { /* private mode */ }
  return fallback;
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(() =>
    readStored<ThemeMode>('gnk-theme', ['dark', 'light'], 'dark')
  );
  const [accent, setAccentState] = useState<ColorAccent>(() =>
    readStored<ColorAccent>('gnk-accent', ['cyan', 'violet', 'rose', 'amber', 'emerald', 'indigo'], 'cyan')
  );

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', mode);
    root.setAttribute('data-accent', accent);
    try {
      localStorage.setItem('gnk-theme', mode);
      localStorage.setItem('gnk-accent', accent);
    } catch { /* ignore */ }
  }, [mode, accent]);

  const toggleMode = () => setModeState((m) => (m === 'dark' ? 'light' : 'dark'));
  const setMode = (m: ThemeMode) => setModeState(m);
  const setAccent = (a: ColorAccent) => setAccentState(a);

  return (
    <ThemeContext.Provider value={{ mode, accent, toggleMode, setMode, setAccent }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
